/**
 * Sidebar icons — one Streamline Flex icon per thing the navigation can point at.
 *
 * WHY THESE ARE COMPONENTS AND NOT <img>
 *
 * The rest of the panel renders Streamline icons through <img> and flips them
 * for dark mode with `dark:invert` (see designSystem/icons.js). That works for
 * a stat card, where the icon is always the same colour. A sidebar icon is not:
 * it is grey at rest, near-black on hover, and the active accent on the current
 * page, in two themes. As <img> that would be six files per icon.
 *
 * So these import as React components (CRA's `ReactComponent` / SVGR) and the
 * artwork's hardcoded `stroke="#000000"` is overridden in CSS — see the
 * `.nav-icon` rule in assets/css/custom.css — to `currentColor`. The icon then
 * simply inherits whatever colour its row is, and every state comes free.
 *
 * NAMING
 *
 * Keys describe *what the destination is about*, not which page it is, so two
 * pages about the same thing always carry the same icon and a rename upstream
 * cannot make them drift. There are fewer keys than menu entries, deliberately:
 * a group and its dashboard share `dashboard`, every list of companies shares
 * `company`.
 *
 * Fourteen of these are drawn in-house (assets/svg/icons/nav) because the
 * licensed set has no equivalent — a gear, a bell, a mortar board and so on.
 * They are drawn to the same spec as the bought ones: an 18x18 box on a
 * -0.645 origin, 1.29 stroke, round caps and joins.
 */

/* Streamline Flex — the licensed set, shared with the stat cards. */
import { ReactComponent as UserGroup } from '../assets/svg/icons/User-Group--Streamline-Flex.svg';
import { ReactComponent as Specialist } from '../assets/svg/icons/Specialist--Streamline-Flex.svg';
import { ReactComponent as CompanyIcon } from '../assets/svg/icons/Company--Streamline-Flex.svg';
import { ReactComponent as PartnerIcon } from '../assets/svg/icons/Partner--Streamline-Flex.svg';
import { ReactComponent as RevenueIcon } from '../assets/svg/icons/Revenue--Streamline-Flex.svg';
import { ReactComponent as SubscriptionIcon } from '../assets/svg/icons/Subscription--Streamline-Flex.svg';
import { ReactComponent as SessionIcon } from '../assets/svg/icons/Session--Streamline-Flex.svg';
import { ReactComponent as FileReport } from '../assets/svg/icons/File-Report--Streamline-Flex.svg';
import { ReactComponent as CheckIcon } from '../assets/svg/icons/Check--Streamline-Flex.svg';
import { ReactComponent as TargetIcon } from '../assets/svg/icons/Target--Streamline-Flex.svg';
import { ReactComponent as Checklist } from '../assets/svg/icons/List-To-Do-Tasks-Checklist--Streamline-Flex.svg';
import { ReactComponent as CalendarMark } from '../assets/svg/icons/Calendar-Mark--Streamline-Flex.svg';
import { ReactComponent as ChatBubble } from '../assets/svg/icons/Chat-Bubble-Text-Oval--Streamline-Flex.svg';
import { ReactComponent as OpenFolder } from '../assets/svg/icons/Open-Folder--Streamline-Flex.svg';
import { ReactComponent as UploadFile } from '../assets/svg/icons/Upload-File--Streamline-Flex.svg';
import { ReactComponent as AlarmClock } from '../assets/svg/icons/Alarm-Clock--Streamline-Flex.svg';
import { ReactComponent as Timeline } from '../assets/svg/icons/Timeline-Roadmap-Horizontal--Streamline-Flex.svg';
import { ReactComponent as DragIcon } from '../assets/svg/icons/Drag.svg';
import { ReactComponent as WarningIcon } from '../assets/svg/icons/Warning.svg';
import { ReactComponent as AddCircle } from '../assets/svg/icons/Add-Circle--Streamline-Flex.svg';

/* Shoorah's own feature artwork, already used by the feature hubs. */
import { ReactComponent as AffirmationIcon } from '../assets/svg/icons/affirmation.svg';
import { ReactComponent as MeditationIcon } from '../assets/svg/icons/meditation.svg';
import { ReactComponent as SleepIcon } from '../assets/svg/icons/sleep_tracker.svg';
import { ReactComponent as PodsIcon } from '../assets/svg/icons/shoorahPods.svg';
import { ReactComponent as VidsIcon } from '../assets/svg/icons/shoorahVids.svg';
import { ReactComponent as MedicalVidsIcon } from '../assets/svg/icons/medicalVids.svg';
import { ReactComponent as ActiveVideosIcon } from '../assets/svg/icons/active_videos.svg';
import { ReactComponent as BreathworkIcon } from '../assets/svg/icons/breathwork.svg';
import { ReactComponent as HabitIcon } from '../assets/svg/icons/habit_tracker.svg';
import { ReactComponent as VisionBoardIcon } from '../assets/svg/icons/vision_board.svg';
import { ReactComponent as MarketplaceIcon } from '../assets/svg/icons/marketplace.svg';
import { ReactComponent as SelfAssessmentIcon } from '../assets/svg/icons/self_assessment.svg';
import { ReactComponent as SelfDevelopmentIcon } from '../assets/svg/icons/self_development.svg';
import { ReactComponent as TopCategoryIcon } from '../assets/svg/icons/top_category.svg';
import { ReactComponent as GoalTrackerIcon } from '../assets/svg/icons/goal_tracker.svg';
import { ReactComponent as GuidedPathIcon } from '../assets/svg/icons/guided_path.svg';
import { ReactComponent as MoodIcon } from '../assets/svg/icons/mood_tracking.svg';
import { ReactComponent as ShuruIcon } from '../assets/svg/icons/shuru.svg';
import { ReactComponent as SupportLineIcon } from '../assets/svg/icons/support_line.svg';
import { ReactComponent as PulseSurveyIcon } from '../assets/svg/icons/pulse_surveys.svg';
import { ReactComponent as InsightTestIcon } from '../assets/svg/icons/insight_tests.svg';
import { ReactComponent as HomeTestingIcon } from '../assets/svg/icons/home_testing.svg';
import { ReactComponent as EliteMindIcon } from '../assets/svg/icons/elite_mind.svg';
import { ReactComponent as MoneyMindIcon } from '../assets/svg/icons/money_mind.svg';
import { ReactComponent as TriggerTraceIcon } from '../assets/svg/icons/triggerTrace.svg';
import { ReactComponent as ChallengesIcon } from '../assets/svg/icons/challenges.svg';
import { ReactComponent as ShoutoutsIcon } from '../assets/svg/icons/shoutouts.svg';
import { ReactComponent as PerksIcon } from '../assets/svg/icons/perks.svg';
import { ReactComponent as BenefitsIcon } from '../assets/svg/icons/benefits.svg';
import { ReactComponent as PeptideIcon } from '../assets/svg/icons/peptide.svg';
import { ReactComponent as ShuffleIcon } from '../assets/svg/icons/shuffle.svg';
import { ReactComponent as SkinScanIcon } from '../assets/svg/icons/skin_scan.svg';
import { ReactComponent as LastPuffIcon } from '../assets/svg/icons/last_puff.svg';
import { ReactComponent as SalarySacrificeIcon } from '../assets/svg/icons/salary_sacrifice.svg';

/* Drawn in-house to fill the gaps — see the note at the top of this file. */
import { ReactComponent as GearIcon } from '../assets/svg/icons/nav/gear.svg';
import { ReactComponent as DocumentIcon } from '../assets/svg/icons/nav/document.svg';
import { ReactComponent as BellIcon } from '../assets/svg/icons/nav/bell.svg';
import { ReactComponent as ShieldIcon } from '../assets/svg/icons/nav/shield.svg';
import { ReactComponent as SchoolIcon } from '../assets/svg/icons/nav/school.svg';
import { ReactComponent as CardIcon } from '../assets/svg/icons/nav/card.svg';
import { ReactComponent as DumbbellIcon } from '../assets/svg/icons/nav/dumbbell.svg';
import { ReactComponent as AppleIcon } from '../assets/svg/icons/nav/apple.svg';
import { ReactComponent as CalculatorIcon } from '../assets/svg/icons/nav/calculator.svg';
import { ReactComponent as UserIcon } from '../assets/svg/icons/nav/user.svg';
import { ReactComponent as PanelIcon } from '../assets/svg/icons/nav/panel.svg';
import { ReactComponent as TileGridIcon } from '../assets/svg/icons/nav/apps.svg';
import { ReactComponent as PodiumIcon } from '../assets/svg/icons/nav/podium.svg';

// Supplied artwork — 24x24, 1.5 stroke. `.nav-icon` recolours the baked
// #0B1D2E strokes to currentColor, so these theme like the rest.
import { ReactComponent as DashboardNavIcon } from '../assets/svg/icons/nav/dashboard.svg';
import { ReactComponent as PeopleNavIcon } from '../assets/svg/icons/nav/people_management.svg';
import { ReactComponent as BusinessNavIcon } from '../assets/svg/icons/nav/business_hub.svg';
import { ReactComponent as ExperienceNavIcon } from '../assets/svg/icons/nav/experience.svg';
import { ReactComponent as ToolsNavIcon } from '../assets/svg/icons/nav/features_tool.svg';
import { ReactComponent as KidsNavIcon } from '../assets/svg/icons/nav/kids_management.svg';
import { ReactComponent as HealthNavIcon } from '../assets/svg/icons/nav/shoorahHealth.svg';
import { ReactComponent as PlayNavIcon } from '../assets/svg/icons/nav/shoorahPlay.svg';
import { ReactComponent as AchievementsNavIcon } from '../assets/svg/icons/nav/achiements.svg';
import { ReactComponent as AdminNavIcon } from '../assets/svg/icons/nav/admin.svg';

export const NAV_ICONS = {
  // Admin
  admin: AdminNavIcon,
  dashboard: DashboardNavIcon,
  approval: CheckIcon,
  cms: DocumentIcon,
  config: GearIcon,
  report: FileReport,

  // People
  people: PeopleNavIcon,
  users: UserGroup,
  experts: Specialist,
  staff: ShieldIcon,
  account: UserIcon,
  school: SchoolIcon,
  peoplePlan: GoalTrackerIcon,
  introduce: PartnerIcon,

  // Business
  business: BusinessNavIcon,
  company: CompanyIcon,
  partners: PartnerIcon,
  earnings: RevenueIcon,
  wellpoints: RevenueIcon,
  payments: CardIcon,
  subscription: SubscriptionIcon,
  salarySacrifice: SalarySacrificeIcon,

  // Features & tools
  tools: ToolsNavIcon,
  peap: SupportLineIcon,
  support: SupportLineIcon,
  specialist: Specialist,
  pulseSurvey: PulseSurveyIcon,
  shoorahTest: InsightTestIcon,
  homeTesting: HomeTestingIcon,
  scans: SkinScanIcon,
  cessation: LastPuffIcon,
  create: AddCircle,
  tutorials: ActiveVideosIcon,
  notifications: BellIcon,
  issues: WarningIcon,
  feedback: ChatBubble,
  legals: DocumentIcon,
  assets: OpenFolder,
  integrations: ShuffleIcon,
  solutions: TargetIcon,
  badges: AchievementsNavIcon,
  perks: PerksIcon,
  benefits: BenefitsIcon,
  challenges: ChallengesIcon,
  shoutouts: ShoutoutsIcon,

  // Experience
  experience: ExperienceNavIcon,
  focus: TargetIcon,
  affirmation: AffirmationIcon,
  meditation: MeditationIcon,
  sleep: SleepIcon,
  pods: PodsIcon,
  habits: HabitIcon,
  topPicks: TopCategoryIcon,
  visionBoard: VisionBoardIcon,
  breathwork: BreathworkIcon,
  vids: VidsIcon,
  marketplace: MarketplaceIcon,
  selfAssessment: SelfAssessmentIcon,
  selfDevelopment: SelfDevelopmentIcon,
  guidedPath: GuidedPathIcon,
  mood: MoodIcon,
  shuru: ShuruIcon,
  moneyMind: MoneyMindIcon,
  triggerTrace: TriggerTraceIcon,

  // Elite games
  games: PlayNavIcon,
  eliteMind: EliteMindIcon,
  quiz: Checklist,
  tasks: Checklist,
  category: TopCategoryIcon,
  order: DragIcon,
  importData: UploadFile,
  dailyQuiz: CalendarMark,
  maths: CalculatorIcon,
  // One per game, so the sidebar group reads as seven destinations rather
  // than the same controller repeated seven times.
  match: TileGridIcon,
  word: ChatBubble,
  learn: SchoolIcon,
  leaderboard: PodiumIcon,

  // Health
  health: HealthNavIcon,
  fitness: DumbbellIcon,
  nutrition: AppleIcon,
  healthVideos: MedicalVidsIcon,
  peptides: PeptideIcon,

  // Kids
  kids: KidsNavIcon,

  // Expert / partner panels
  schedule: CalendarMark,
  sessions: SessionIcon,
  clients: UserGroup,
  timeline: Timeline,
  time: AlarmClock,

  // Chrome
  panel: PanelIcon
};

export default NAV_ICONS;
